import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './CreatePost.module.scss';

interface CreatePostProps {
  onCreatePost: (content: string, file?: File) => void;
}

const CreatePost: React.FC<CreatePostProps> = ({ onCreatePost }) => {
  const [content, setContent] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const isAuthenticated = !!localStorage.getItem('token');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;

    if (!isAuthenticated) {
      setError('Please log in to create a post');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await onCreatePost(content, selectedFile || undefined);
      setContent('');
      setSelectedFile(null);
    } catch (error) {
      console.error('Error creating post:', error);
      setError('Failed to create post. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={styles.createPost}>
      <form onSubmit={handleSubmit} className={styles.createPostForm}>
        <div className={styles.postInputContainer}>
          <div className={styles.userAvatar}>
            <img 
              src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=96&h=96&fit=crop&crop=face" 
              alt="User Avatar"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=96&h=96&fit=crop&crop=face";
              }}
            />
          </div>
          
          <div className={styles.postInputWrapper}>
            {!isAuthenticated && (
              <div className={styles.loginPrompt}>
                <p>Please log in to create a post</p>
                <button 
                  type="button" 
                  className={styles.loginBtn}
                  onClick={() => navigate('/login')}
                >
                  Log In
                </button>
              </div>
            )}
            
            <textarea
              className={`${styles.postInput} ${!isAuthenticated ? styles.disabled : ''}`}
              placeholder={isAuthenticated ? "What's on your mind?" : "Please log in to create a post"}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={3}
              maxLength={500}
              disabled={!isAuthenticated}
            />
            
            {selectedFile && (
              <div className={styles.selectedFile}>
                <div className={styles.filePreview}>
                  {selectedFile.type.startsWith('image/') ? (
                    <img 
                      src={URL.createObjectURL(selectedFile)} 
                      alt="Preview" 
                      className={styles.fileImage}
                    />
                  ) : (
                    <div className={styles.fileIcon}>📎</div>
                  )}
                  <span className={styles.fileName}>{selectedFile.name}</span>
                </div>
                <button 
                  type="button" 
                  className={styles.removeFileBtn}
                  onClick={removeFile}
                >
                  ✕
                </button>
              </div>
            )}
            
            {error && (
              <div className={styles.errorMessage}>
                {error}
              </div>
            )}
            
            <div className={styles.postActions}>
              <div className={styles.postAttachments}>
                <button
                  type="button"
                  className={styles.attachmentBtn}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!isAuthenticated}
                >
                  <span className={styles.btnIcon}>📷</span>
                  Photo
                </button>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  disabled={!isAuthenticated}
                />
              </div>
              
              <div className={styles.postSubmit}>
                <span className={styles.charCount}>
                  {content.length}/500
                </span>
                <button
                  type="submit"
                  className={styles.submitBtn}
                  disabled={!content.trim() || isSubmitting || !isAuthenticated}
                >
                  {isSubmitting ? 'Posting...' : 'Post'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreatePost; 